import { Fragment, useEffect, useMemo, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { FormHeader1, FormHeader2, FormHeader3 } from '../theme/elements/Headers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { Avatar, Box, Button, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { CenteredNarrowContent } from 'theme/elements/Content';
import { FormButton } from 'theme/elements/Buttons';
import { invalidFields } from 'components/FormCreator/types';
import {
	AccountEntityType,
	NewAccountFieldsType,
	companyType,
	newAccountFieldsValidations,
	newAccountInvalidFieldLabels,
} from 'constants/new_accounts.constants';
import _ from 'lodash';
import { setListOfInvalids } from 'components/FormCreator/validation';
import { Stepper } from 'sections/NewAccounts/Add/Stepper';
import { BasicCard } from 'components/BasicCard';
import { ButtonComponent } from 'components/ButtonComponent';
import { PopupComponent } from 'components/PopupComponent';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {
	getError,
	getIsDirty,
	getNewAccount,
	resetNewAccount,
	setError,
	setNewAccount,
	setNewAccountById,
} from 'state/reducers/new_accounts.reducers';
import { useParams } from 'react-router-dom';
import { getNewAccountById, getNewBankAccountByAccountId, getNewUsersByAccountId } from 'services/new_accounts.service';
import { WarningDialog } from 'components/WarningDialog';
import { format } from 'date-fns';
import { BONotesDrawer } from 'components/BoNotesDrawer';

interface INewAccountsAppProps {
	isEditMode?: boolean;
	newAccountIdToEdit?: number;
}

export const NewAccountsApp = (props: INewAccountsAppProps) => {
	const t = useAppSelector(selectTranslations);
	const isDirty = useAppSelector(getIsDirty);
	const dispatch = useAppDispatch();
	const [attentionModalOpen, setAttentionModalOpen] = useState(false);
	const [isApproveMode, setIsApproveMode] = useState(false);
	const theme = useTheme();
	const { action, id } = useParams();
	const newAccountError = useAppSelector(getError);
	const newAccount = useAppSelector(getNewAccount);
	const [open, setOpen] = useState(false);
	const basicButtonStyle = { borderRadius: '8px', width: '320px', height: '48px' };

	const toggleDrawer = () => {
		setOpen(!open);
	};

	useEffect(() => {
		dispatch(resetNewAccount());
		if (id) {
			dispatch(getNewAccountById(id));
			dispatch(getNewUsersByAccountId(id));
			dispatch(getNewBankAccountByAccountId(id));
		}
		setIsApproveMode(action === 'review');
	}, [action, id]);

	const handleAddCustomer = () => {
		setAttentionModalOpen(true);
	};

	const openDialog = useMemo(() => {
		return newAccountError?.exist;
	}, [newAccountError]);

	const errorSubTitle = useMemo(() => {
		const errorText = t(newAccountError?.text || '');
		if (!_.isArray(newAccountError?.subText)) {
			return errorText;
		}
		const fieldLabels = newAccountError.subText.map((field: string) =>
			field === 'dateOfIncorporation' && newAccount?.entityType === AccountEntityType.COMPANY
				? t('label.birthDate')
				: t(newAccountInvalidFieldLabels[field])
		);
		return `${errorText} ${fieldLabels.join(', ')}`;
	}, [newAccountError, newAccount?.entityType, t]);

	return (
		<Page>
			<Fragment>
				<WarningDialog
					open={openDialog}
					subTitle={errorSubTitle}
					handleCancel={() => {
						dispatch(setError({ exist: false }));
					}}
				/>
				<Typography variant="pageTitle">{t('page.accounts.new_account.header.title')}</Typography>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
					{action === 'create' && (
						<ButtonComponent
							disabled={isDirty.dirty}
							handleClick={handleAddCustomer}
							text={`${t('page.accounts.new_account.add_customer')}`}
							icon={
								<img
									src={'/icons/plus-sign.svg'}
									style={{
										marginLeft: '10px',
									}}
								/>
							}
							styleObj={{ flexDirection: 'row-reverse' }}
						/>
					)}
				</Box>

				{id && newAccount?.allDenyMessages?.length ? (
					<BONotesDrawer toggleDrawer={toggleDrawer} open={open} newAccount={newAccount} />
				) : (
					''
				)}
				<Stepper isApproveMode={isApproveMode} />
				<Fragment>
					<Stack spacing={1} sx={{ position: 'relative', height: '100%' }}>
						<CenteredNarrowContent>
							<>
								{attentionModalOpen && (
									<PopupComponent
										open
										title={t('page.accounts.new_account.popup.attention')}
										subtitle={t('page.accounts.new_account.popup.subtitle')}
										isDefaultButton={false}
									>
										<ButtonComponent
											variantType={'contained'}
											disabled={false}
											handleClick={() => {
												dispatch(resetNewAccount());
												setAttentionModalOpen(false);
											}}
											text={t('button.ok')}
											styleObj={{ ...basicButtonStyle }}
										/>
										<ButtonComponent
											variantType={'text'}
											disabled={false}
											handleClick={() => setAttentionModalOpen(false)}
											text={`${t('button.cancel')}`}
											styleObj={{
												...basicButtonStyle,
												'&:hover': {
													background: theme.palette.root.basicDeeoGreen,
													boxShadow: 'none',
													color: 'white',
												},
											}}
										/>
									</PopupComponent>
								)}
							</>
						</CenteredNarrowContent>
					</Stack>
				</Fragment>
			</Fragment>
		</Page>
	);
};
